// Templates: saved authority settings to give the next person in one click.
import React, { useState } from 'react';
import { Plus, Pencil, Copy, Trash2, BookmarkCheck } from 'lucide-react';
import { teamApi, describePermissions } from '../../services/account';
import { Button, Modal, Notice, formatWhen } from './ui';

const TemplatesTab = ({ options, templates, onEdit, onReload, flash }) => {
  const [confirm, setConfirm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const features = options?.features || [];
  // brand id -> kind, to count a template's brands per kind.
  const kindById = {};
  ['projects', 'competitors', 'gaps'].forEach((kind) => (options?.brands?.[kind] || []).forEach((b) => { kindById[b.id] = kind; }));
  const countsFor = (ids = []) => ids.reduce((acc, id) => {
    const kind = kindById[id];
    if (kind) acc[kind] = (acc[kind] || 0) + 1;
    return acc;
  }, {});

  const duplicate = async (t) => {
    setError('');
    try {
      let name = `${t.name} (copy)`;
      const names = new Set(templates.map((x) => x.name));
      for (let i = 2; names.has(name); i += 1) name = `${t.name} (copy ${i})`;
      await teamApi.createTemplate({ name, description: t.description, permissions: t.permissions, brandIds: t.brand_ids || [] });
      flash(`Created "${name}"`);
      await onReload();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await teamApi.deleteTemplate(confirm.id);
      flash(`Deleted "${confirm.name}"`);
      await onReload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Save a set of permissions once, then pick it when you add someone or apply it to several people.
        </p>
        <Button onClick={() => onEdit(null)}><Plus className="w-4 h-4" /> New template</Button>
      </div>
      {error && <Notice>{error}</Notice>}

      {!templates.length ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-slate-700 p-10 text-center">
          <BookmarkCheck className="w-8 h-8 mx-auto text-gray-300 dark:text-slate-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
            No templates yet. Create one here, or use "Save as template" inside someone's Authority panel.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-slate-100 truncate">{t.name}</h3>
                  {t.description && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{t.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => onEdit(t)} className="p-1.5 rounded-md text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button type="button" onClick={() => duplicate(t)} className="p-1.5 rounded-md text-gray-400 hover:text-blue-600" title="Duplicate"><Copy className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setConfirm(t)} className="p-1.5 rounded-md text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-gray-600 dark:text-slate-300">
                {describePermissions(t.permissions, features, countsFor(t.brand_ids || []))}
              </p>
              <p className="mt-2 text-[11px] text-gray-400">
                Used by {t.member_count || 0} {t.member_count === 1 ? 'person' : 'people'} · updated {formatWhen(t.updated_at)}
              </p>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={Boolean(confirm)}
        title="Delete template?"
        onClose={() => setConfirm(null)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="danger" disabled={busy} onClick={remove}>{busy ? 'Deleting…' : 'Delete'}</Button>
          </>
        )}
      >
        <p className="text-sm text-gray-600 dark:text-slate-300">
          "{confirm?.name}" will be deleted. People who were given it keep their current permissions.
        </p>
      </Modal>
    </div>
  );
};

export default TemplatesTab;
